import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Download, Grid3X3, Palette, PenTool, X } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = [
  '#00FFFF','#FF0000','#FF00FF','#0000FF','#00FF00','#FFFF00',
  '#FF8000','#8000FF','#0080FF','#FF0080','#80FF00','#00FF80',
  '#FF4040','#40FF40','#4040FF','#C00000','#00C000','#0000C0',
  '#800000','#008000','#000080','#808000','#800080','#008080',
  '#FF6060','#60FF60','#6060FF','#C0C000','#C000C0','#00C0C0',
  '#FF8080','#80FF80','#8080FF','#FFFF80','#FF80FF','#80FFFF',
  '#E04000','#00E040','#4000E0','#E0E000','#E000E0','#00E0E0',
  '#A00000','#00A000','#0000A0','#A0A000','#A000A0','#00A0A0',
  '#FF2020','#20FF20','#2020FF','#FFAA00','#AA00FF','#00AAFF',
  '#CC3333','#33CC33','#3333CC','#CCCC33','#CC33CC','#33CCCC',
  '#FF6600','#6600FF','#00FF66','#FF0066','#0066FF','#66FF00',
  '#993399','#339933','#333399','#999933','#993333','#339999',
  '#DD5500','#55DD00','#0055DD','#DD0055','#5500DD','#00DD55',
  '#BB0000','#00BB00','#0000BB','#BB00BB','#BBBB00','#00BBBB',
  '#FF9933','#9933FF','#33FF99','#FF3399','#3399FF','#99FF33',
  '#664400','#446600','#004466','#660044','#440066','#006644',
];

const SmartUVExport = ({ object, scene, onClose }) => {
  const [width, setWidth] = useState(4000);
  const [widthInput, setWidthInput] = useState('4000');
  const [showGrid, setShowGrid] = useState(false);
  const [showColorId, setShowColorId] = useState(true);
  const [showOutline, setShowOutline] = useState(false);
  const [gridSpacing, setGridSpacing] = useState(100);
  const [gridSpacingInput, setGridSpacingInput] = useState('100');
  const [exporting, setExporting] = useState(false);

  const BABYLON = window.__BABYLON;

  // Walk up from a mesh to find the model root (highest parent)
  const findModelRoot = useCallback((mesh) => {
    let root = mesh;
    while (root.parent) root = root.parent;
    return root;
  }, []);

  // Collect all valid renderable meshes from a root node
  const collectMeshes = useCallback((root) => {
    const candidates = root.getChildMeshes ? root.getChildMeshes(false) : [];
    if (root.getVerticesData && !candidates.includes(root)) candidates.unshift(root);
    return candidates.filter(m => {
      if (!m.getVerticesData || !m.isEnabled() || !m.isVisible) return false;
      if (m.visibility <= 0.5 || m.getTotalVertices() === 0) return false;
      return true;
    });
  }, []);

  const meshData = useMemo(() => {
    if (!object || !scene || !BABYLON) return { aspect: 1, mesh: null };
    const mesh = findMesh(scene, object.id);
    if (!mesh) return { aspect: 1, mesh: null };
    const modelRoot = findModelRoot(mesh);
    const children = collectMeshes(modelRoot);
    let minX=Infinity, maxX=-Infinity, minY=Infinity, maxY=-Infinity, minZ=Infinity, maxZ=-Infinity;
    for (const m of children) {
      m.computeWorldMatrix(true);
      const bb = m.getBoundingInfo()?.boundingBox;
      if (!bb || !isFinite(bb.minimumWorld.x)) continue;
      minX = Math.min(minX, bb.minimumWorld.x); maxX = Math.max(maxX, bb.maximumWorld.x);
      minY = Math.min(minY, bb.minimumWorld.y); maxY = Math.max(maxY, bb.maximumWorld.y);
      minZ = Math.min(minZ, bb.minimumWorld.z); maxZ = Math.max(maxZ, bb.maximumWorld.z);
    }
    if (!isFinite(minX)) return { aspect: 1, mesh };
    const w = Math.max(maxX - minX, maxZ - minZ, 0.01);
    const h = Math.max(maxY - minY, 0.01);
    return { aspect: h / w, mesh, modelRoot };
  }, [object, scene, BABYLON, findModelRoot, collectMeshes]);

  const height = Math.round(width * meshData.aspect);

  const handleExport = useCallback(async () => {
    if (!meshData.mesh || !scene || !BABYLON) { toast.error('Mesh not found'); return; }
    setExporting(true);

    try {
      // ─── Step 1: Find model root → collect ALL building meshes ───
      const modelRoot = meshData.modelRoot || findModelRoot(meshData.mesh);
      const childMeshes = collectMeshes(modelRoot);

      console.log(`[UV Export] Root: "${modelRoot.name}", meshes found: ${childMeshes.length}`);
      if (childMeshes.length === 0) { toast.error('No valid geometry found'); setExporting(false); return; }

      // ─── Step 2: Extract world vertices, split by SUB-MESHES for per-element coloring ───
      const meshGeoData = [];
      let gMinX = Infinity, gMaxX = -Infinity;
      let gMinY = Infinity, gMaxY = -Infinity;
      let gMinZ = Infinity, gMaxZ = -Infinity;
      let colorIdx = 0;

      for (const m of childMeshes) {
        m.computeWorldMatrix(true);
        const positions = m.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        const allIndices = m.getIndices();
        if (!positions || positions.length < 3) continue;

        const worldMatrix = m.getWorldMatrix();
        const worldVerts = [];
        for (let i = 0; i < positions.length; i += 3) {
          const local = new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]);
          const world = BABYLON.Vector3.TransformCoordinates(local, worldMatrix);
          worldVerts.push(world);
        }

        // Split by sub-meshes (each gets a unique color)
        const subMeshes = m.subMeshes;
        const groups = (subMeshes && subMeshes.length > 0)
          ? subMeshes.map(sm => ({ start: sm.indexStart, count: sm.indexCount }))
          : [{ start: 0, count: allIndices ? allIndices.length : 0 }];

        for (const g of groups) {
          const subIndices = allIndices
            ? Array.from(allIndices).slice(g.start, g.start + g.count)
            : Array.from({ length: positions.length / 3 }, (_, i) => i);
          if (subIndices.length < 3) continue;

          // Track bounds from indexed vertices
          const usedSet = new Set(subIndices);
          for (const vi of usedSet) {
            if (vi >= worldVerts.length) continue;
            const wp = worldVerts[vi];
            gMinX = Math.min(gMinX, wp.x); gMaxX = Math.max(gMaxX, wp.x);
            gMinY = Math.min(gMinY, wp.y); gMaxY = Math.max(gMaxY, wp.y);
            gMinZ = Math.min(gMinZ, wp.z); gMaxZ = Math.max(gMaxZ, wp.z);
          }
          meshGeoData.push({ worldVerts, indices: subIndices, colorIdx: colorIdx++ });
        }
      }

      if (meshGeoData.length === 0) { toast.error('No geometry data extracted'); setExporting(false); return; }

      const extX = gMaxX - gMinX;
      const extZ = gMaxZ - gMinZ;
      const useXView = extZ > extX * 1.5;

      console.log(`[UV Export] Sub-groups: ${meshGeoData.length} (${colorIdx} colors), extX=${extX.toFixed(2)}, extZ=${extZ.toFixed(2)}, useXView=${useXView}`);

      // Sort meshes by depth — painter's algorithm: draw farthest first, nearest last
      // This ensures front-facing details aren't hidden by back walls
      meshGeoData.sort((a, b) => {
        const depthA = a.worldVerts.reduce((s, v) => s + (useXView ? v.x : v.z), 0) / (a.worldVerts.length || 1);
        const depthB = b.worldVerts.reduce((s, v) => s + (useXView ? v.x : v.z), 0) / (b.worldVerts.length || 1);
        return depthA - depthB;
      });

      // ─── Step 3: Project to 2D (bounds from INDEXED verts only) ───
      let min2DX = Infinity, max2DX = -Infinity;
      let min2DY = Infinity, max2DY = -Infinity;

      for (const data of meshGeoData) {
        data.projected = data.worldVerts.map(wp => {
          const px = useXView ? wp.z : wp.x;
          const py = wp.y;
          return { px, py };
        });
        const usedSet = new Set(data.indices);
        for (const vi of usedSet) {
          const p = data.projected[vi];
          if (!p) continue;
          min2DX = Math.min(min2DX, p.px); max2DX = Math.max(max2DX, p.px);
          min2DY = Math.min(min2DY, p.py); max2DY = Math.max(max2DY, p.py);
        }
      }

      const range2DX = max2DX - min2DX || 1;
      const range2DY = max2DY - min2DY || 1;

      // ─── Step 4: Canvas sized to actual content aspect ratio ───
      const actualAspect = range2DY / range2DX;
      const canvasW = width;
      const canvasH = Math.max(Math.round(width * actualAspect), 1);

      console.log(`[UV Export] 2D: [${min2DX.toFixed(2)}..${max2DX.toFixed(2)}] x [${min2DY.toFixed(2)}..${max2DY.toFixed(2)}], canvas: ${canvasW}x${canvasH}`);

      // ─── Step 5: Normalize → canvas coords ───
      const pad = 0.01;
      const usableW = canvasW * (1 - 2 * pad);
      const usableH = canvasH * (1 - 2 * pad);
      const offX = canvasW * pad;
      const offY = canvasH * pad;
      const toX = (px) => ((px - min2DX) / range2DX) * usableW + offX;
      const toY = (py) => (1 - (py - min2DY) / range2DY) * usableH + offY;

      // ─── Step 6: Draw on 2D canvas ───
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvasW, canvasH);

      if (showColorId) {
        let globalColor = 0;
        for (const data of meshGeoData) {
          const { projected, indices } = data;
          const triCount = Math.floor(indices.length / 3);
          if (triCount === 0) continue;

          // ── Union-Find to detect connected components (separate elements) ──
          const uf = new Int32Array(triCount);
          for (let i = 0; i < triCount; i++) uf[i] = i;
          const find = (x) => { while (uf[x] !== x) { uf[x] = uf[uf[x]]; x = uf[x]; } return x; };
          const unite = (a, b) => { uf[find(a)] = find(b); };

          // Build edge → triangle map, then union triangles sharing an edge
          const edgeMap = new Map();
          for (let t = 0; t < triCount; t++) {
            const v0 = indices[t * 3], v1 = indices[t * 3 + 1], v2 = indices[t * 3 + 2];
            const edges = [
              Math.min(v0,v1) + '_' + Math.max(v0,v1),
              Math.min(v1,v2) + '_' + Math.max(v1,v2),
              Math.min(v0,v2) + '_' + Math.max(v0,v2),
            ];
            for (const key of edges) {
              if (edgeMap.has(key)) { unite(edgeMap.get(key), t); }
              else { edgeMap.set(key, t); }
            }
          }

          // Assign unique color per connected component
          const compColorMap = new Map();
          for (let t = 0; t < triCount; t++) {
            const root = find(t);
            if (!compColorMap.has(root)) compColorMap.set(root, globalColor++);
            const ci = compColorMap.get(root);
            ctx.fillStyle = COLORS[ci % COLORS.length];

            const p0 = projected[indices[t * 3]];
            const p1 = projected[indices[t * 3 + 1]];
            const p2 = projected[indices[t * 3 + 2]];
            if (!p0 || !p1 || !p2) continue;
            ctx.beginPath();
            ctx.moveTo(toX(p0.px), toY(p0.py));
            ctx.lineTo(toX(p1.px), toY(p1.py));
            ctx.lineTo(toX(p2.px), toY(p2.py));
            ctx.closePath();
            ctx.fill();
          }
        }
        console.log(`[UV Export] Connected components: ${globalColor} unique color groups`);
      }

      if (showOutline) {
        ctx.strokeStyle = 'rgba(255,255,255,0.7)';
        ctx.lineWidth = 1;
        for (const { projected, indices } of meshGeoData) {
          for (let i = 0; i + 2 < indices.length; i += 3) {
            const p0 = projected[indices[i]];
            const p1 = projected[indices[i + 1]];
            const p2 = projected[indices[i + 2]];
            if (!p0 || !p1 || !p2) continue;
            ctx.beginPath();
            ctx.moveTo(toX(p0.px), toY(p0.py));
            ctx.lineTo(toX(p1.px), toY(p1.py));
            ctx.lineTo(toX(p2.px), toY(p2.py));
            ctx.closePath();
            ctx.stroke();
          }
        }
      }

      if (showGrid) {
        ctx.strokeStyle = 'rgba(255,255,255,0.15)';
        ctx.lineWidth = 1;
        for (let x = 0; x < canvasW; x += gridSpacing) {
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvasH); ctx.stroke();
        }
        for (let y = 0; y < canvasH; y += gridSpacing) {
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvasW, y); ctx.stroke();
        }
      }

      const link = document.createElement('a');
      link.download = `${object.name || 'uv'}_template_${canvasW}x${canvasH}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(`UV template exported (${canvasW}x${canvasH}px)`);
    } catch (err) {
      console.error('UV export failed:', err);
      toast.error('Export failed: ' + err.message);
    }
    setExporting(false);
  }, [meshData, scene, BABYLON, width, showGrid, showColorId, showOutline, gridSpacing, object, findModelRoot, collectMeshes]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-5 w-[380px] space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold text-white">Smart UV Export</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0"><X className="w-4 h-4" /></Button>
        </div>
        <div className="text-[10px] text-zinc-400">Object: {object?.name} — Front orthographic projection</div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-zinc-500">Width (px)</Label>
            <Input type="number" value={widthInput} min={512} max={16384} step={100}
              onChange={(e) => setWidthInput(e.target.value)}
              onBlur={() => {
                const v = parseInt(widthInput, 10);
                const clamped = isNaN(v) ? 4000 : Math.max(512, Math.min(16384, v));
                setWidth(clamped);
                setWidthInput(String(clamped));
              }}
              className="h-7 text-xs bg-zinc-800 border-zinc-700" />
          </div>
          <div>
            <Label className="text-[10px] text-zinc-500">Height (auto)</Label>
            <Input type="number" value={height} readOnly className="h-7 text-xs bg-zinc-800 border-zinc-600 text-zinc-400" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Palette className="w-3.5 h-3.5 text-fuchsia-400" /><span className="text-xs">Color ID (per element)</span></div>
            <Switch checked={showColorId} onCheckedChange={setShowColorId} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><PenTool className="w-3.5 h-3.5 text-green-400" /><span className="text-xs">Wireframe Outline</span></div>
            <Switch checked={showOutline} onCheckedChange={setShowOutline} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Grid3X3 className="w-3.5 h-3.5 text-cyan-400" /><span className="text-xs">Grid Overlay</span></div>
            <Switch checked={showGrid} onCheckedChange={setShowGrid} />
          </div>
          {showGrid && (
            <div className="ml-6"><Label className="text-[10px] text-zinc-500">Spacing (px)</Label>
              <Input type="number" value={gridSpacingInput} min={10} max={500} step={10}
                onChange={(e) => setGridSpacingInput(e.target.value)}
                onBlur={() => {
                  const v = parseInt(gridSpacingInput, 10);
                  const clamped = isNaN(v) ? 100 : Math.max(10, Math.min(500, v));
                  setGridSpacing(clamped);
                  setGridSpacingInput(String(clamped));
                }}
                className="h-6 text-xs bg-zinc-800 border-zinc-700 w-24" /></div>
          )}
        </div>

        <Button onClick={handleExport} disabled={exporting}
          className="w-full h-9 bg-fuchsia-600 hover:bg-fuchsia-700 text-xs font-medium" data-testid="smart-uv-export-btn">
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Capturing...' : `Export PNG (${width} x ${height})`}
        </Button>
      </div>
    </div>
  );
};

function findMesh(scene, id) {
  let mesh = scene.getMeshByName('obj_' + id) || scene.getTransformNodeByName('obj_' + id)
    || scene.getMeshByName(id) || scene.getTransformNodeByName(id);
  if (!mesh) { for (const m of scene.meshes) { if (m.metadata?.id === id) { mesh = m; break; } } }
  if (!mesh) { for (const n of scene.transformNodes) { if (n.metadata?.id === id || n.name?.includes(id)) { mesh = n; break; } } }
  return mesh;
}

// findMesh helper only

export default SmartUVExport;
